import { ToolCall } from "../types";
import { ProcessedQueryData } from "./types";

/**
 * Derive a display name from a custom MCP server slug.
 * Strips the "mcp_" prefix and converts to title case (e.g. "mcp_rootly-mcp" → "Rootly Mcp").
 * Returns null if the slug is empty/missing.
 */
export function getCustomMcpServerDisplayName(serverSlug: string | undefined | null): string | null {
  if (!serverSlug) return null;
  const bare = serverSlug.replace(/^mcp_/, "");
  if (!bare) return null;
  return bare.split(/[-_]/).map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

/**
 * Check if a tool input is a V2 message (GenericToolInput with rawInput)
 */
export function isV2ToolInput(input: any): boolean {
  return input?.__typename === "GenericToolInput" && input?.rawInput != null;
}

/**
 * Parse V2 rawInput to extract query data for display
 */
export function processV2QueryData(rawInput: any, toolName: string): ProcessedQueryData {
  try {
    // Parse rawInput if it's a string
    const parsedRawInput = typeof rawInput === "string" ? JSON.parse(rawInput) : rawInput;
    const filteredInput = filterQueryForDisplay(parsedRawInput);

    let result: ProcessedQueryData;

    // Use tool name to deterministically process query data
    switch (toolName) {
      // ===== DATADOG TOOLS =====
      case "mcp__tierzero__query_datadog_metric_tags": {
        result = {
          displayData: `metric_name:${parsedRawInput.metric_name}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_datadog_logs":
      case "mcp__tierzero__query_datadog_logs_aggregation":
      case "mcp__tierzero__query_datadog_spans":
      case "mcp__tierzero__query_datadog_spans_aggregation":
      case "mcp__tierzero__query_datadog_ci_pipeline_events":
      case "mcp__tierzero__query_datadog_ci_pipeline_events_aggregation":
      case "mcp__tierzero__query_datadog_rum_aggregation":
      case "mcp__tierzero__query_datadog_events":
      case "mcp__tierzero__search_datadog_metrics": {
        // Tools with query field that might be JSON or plain text
        const queryRaw = parsedRawInput.query;
        if (!queryRaw) {
          return { displayData: "", rawData: "", isJson: false };
        }

        // Try to parse as JSON
        if (typeof queryRaw === "string") {
          try {
            const queryObj = JSON.parse(queryRaw);
            const filteredJson = filterQueryForDisplay(queryObj);

            // Check if it has filter.query which should be shown in the header
            if (queryObj.filter && queryObj.filter.query) {
              result = {
                displayData: queryObj.filter.query,
                rawData: JSON.stringify(filteredJson, null, 2),
                isJson: true,
              };
            } else {
              // No filter.query, use the full JSON
              const formattedJson = JSON.stringify(filteredJson, null, 2);
              result = {
                displayData: formattedJson.replace(/\n/g, "").replace(/\s+/g, " "),
                rawData: formattedJson,
                isJson: true,
              };
            }
          } catch {
            // Not JSON - it's a plain text query
            result = {
              displayData: queryRaw,
              rawData: JSON.stringify(filteredInput, null, 2),
              isJson: true,
            };
          }
        } else {
          // Query is already an object
          const filteredJson = filterQueryForDisplay(queryRaw);
          if (queryRaw.filter && queryRaw.filter.query) {
            result = {
              displayData: queryRaw.filter.query,
              rawData: JSON.stringify(filteredJson, null, 2),
              isJson: true,
            };
          } else {
            const formattedJson = JSON.stringify(filteredJson, null, 2);
            result = {
              displayData: formattedJson.replace(/\n/g, "").replace(/\s+/g, " "),
              rawData: formattedJson,
              isJson: true,
            };
          }
        }
        break;
      }

      case "mcp__tierzero__query_datadog_error_tracking_issue_details": {
        result = {
          displayData: `issue_id:${parsedRawInput.issue_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_datadog_error_tracking_issues": {
        const queryDisplay = parsedRawInput.query || "";
        result = {
          displayData: queryDisplay,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_datadog_dashboard": {
        const dashboardIdentifier = parsedRawInput.dashboard_slug
          ? `dashboard_slug:${parsedRawInput.dashboard_slug}`
          : `dashboard_id:${parsedRawInput.dashboard_id}`;
        result = {
          displayData: dashboardIdentifier,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_datadog_notebook": {
        result = {
          displayData: `notebook_id:${parsedRawInput.notebook_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__search_datadog_notebooks": {
        result = {
          displayData: parsedRawInput.query ? `query:${parsedRawInput.query}` : "(all notebooks)",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_datadog_monitor": {
        result = {
          displayData: `monitor_id:${parsedRawInput.monitor_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_datadog_incident": {
        result = {
          displayData: `incident_id:${parsedRawInput.incident_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== DATADOG SYNTHETIC TESTS =====
      case "mcp__tierzero__search_datadog_synthetic_tests": {
        result = {
          displayData: parsedRawInput.query ? `query:${parsedRawInput.query}` : "(all tests)",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_datadog_synthetic_test":
      case "mcp__tierzero__query_datadog_synthetic_test_results": {
        result = {
          displayData: `public_id:${parsedRawInput.public_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_datadog_synthetic_test_result": {
        result = {
          displayData: `public_id:${parsedRawInput.public_id} result_id:${parsedRawInput.result_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_datadog_synthetic_ci_batch": {
        result = {
          displayData: `batch_id:${parsedRawInput.batch_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_datadog_service_dependency": {
        result = {
          displayData: `service:${parsedRawInput.service_name}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_datadog_metrics": {
        // Metrics query is plain text
        const queryRaw = parsedRawInput.query;
        result = {
          displayData: queryRaw || "",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== BUILDKITE TOOLS =====
      case "mcp__tierzero__query_buildkite_builds":
      case "mcp__tierzero__query_buildkite_logs":
      case "mcp__tierzero__query_buildkite_annotations":
      case "mcp__tierzero__query_buildkite_artifacts": {
        const displayParts: string[] = [`pipeline:${parsedRawInput.pipeline_slug}`];

        if (parsedRawInput.build_number) {
          displayParts.push(`build:${parsedRawInput.build_number}`);
        }
        if (parsedRawInput.query_params) {
          displayParts.push(`params:${parsedRawInput.query_params}`);
        }
        if (parsedRawInput.state_filter && Array.isArray(parsedRawInput.state_filter)) {
          displayParts.push(`states:${parsedRawInput.state_filter.join(", ")}`);
        }
        if (parsedRawInput.retry_type_filter && Array.isArray(parsedRawInput.retry_type_filter)) {
          displayParts.push(`retry_types:${parsedRawInput.retry_type_filter.join(", ")}`);
        }
        if (parsedRawInput.context_filter && Array.isArray(parsedRawInput.context_filter)) {
          displayParts.push(`contexts:${parsedRawInput.context_filter.join(", ")}`);
        }
        if (parsedRawInput.artifact_path) {
          displayParts.push(`artifact:${parsedRawInput.artifact_path}`);
        }
        if (parsedRawInput.search_query) {
          displayParts.push(`search:"${parsedRawInput.search_query}"`);
        }
        if (parsedRawInput.step_key) {
          displayParts.push(`step_key:${parsedRawInput.step_key}`);
        }
        if (parsedRawInput.job_name) {
          displayParts.push(`job_name:${parsedRawInput.job_name}`);
        }

        result = {
          displayData: displayParts.join(" "),
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_buildkite_flaky_test": {
        result = {
          displayData: `suite:${parsedRawInput.test_suite_slug} | test:${parsedRawInput.test_name}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== CLOUDWATCH TOOLS =====
      case "mcp__tierzero__query_cloudwatch_logs":
      case "mcp__tierzero__query_cloudwatch_logs_aggregation": {
        const queryRaw = parsedRawInput.query;
        if (!queryRaw) {
          result = { displayData: "", rawData: "", isJson: false };
          break;
        }

        // CloudWatch query could be a plain string or JSON object
        if (typeof queryRaw === "string") {
          try {
            // Try to parse as JSON
            const queryObj = JSON.parse(queryRaw);
            const filteredJson = filterQueryForDisplay(queryObj);

            if (queryObj.filter && queryObj.filter.query) {
              // Extract filter.query for display with accordion
              result = {
                displayData: queryObj.filter.query,
                rawData: JSON.stringify(filteredJson, null, 2),
                isJson: true,
              };
            } else {
              // Show formatted JSON
              const formattedJson = JSON.stringify(filteredJson, null, 2);
              result = {
                displayData: formattedJson.replace(/\n/g, "").replace(/\s+/g, " "),
                rawData: formattedJson,
                isJson: true,
              };
            }
          } catch {
            // Not JSON - it's a plain CloudWatch Insights query string
            // Use accordion format for long queries
            result = {
              displayData: queryRaw,
              rawData: queryRaw,
              isJson: true,
            };
          }
        } else {
          // Query is already an object
          const filteredJson = filterQueryForDisplay(queryRaw);
          if (queryRaw.filter && queryRaw.filter.query) {
            result = {
              displayData: queryRaw.filter.query,
              rawData: JSON.stringify(filteredJson, null, 2),
              isJson: true,
            };
          } else {
            const formattedJson = JSON.stringify(filteredJson, null, 2);
            result = {
              displayData: formattedJson.replace(/\n/g, "").replace(/\s+/g, " "),
              rawData: formattedJson,
              isJson: true,
            };
          }
        }
        break;
      }

      case "mcp__tierzero__query_cloudwatch_metrics": {
        const queryRaw = parsedRawInput.query;
        result = {
          displayData: queryRaw || "",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== GRAFANA CLOUD TOOLS =====
      case "mcp__tierzero__query_grafana_cloud_logs":
      case "mcp__tierzero__query_grafana_cloud_logs_aggregation":
      case "mcp__tierzero__query_grafana_cloud_spans":
      case "mcp__tierzero__query_grafana_cloud_spans_aggregation": {
        const queryRaw = parsedRawInput.query;
        if (!queryRaw) {
          result = { displayData: "", rawData: "", isJson: false };
          break;
        }

        if (typeof queryRaw === "string") {
          try {
            const queryObj = JSON.parse(queryRaw);
            const filteredJson = filterQueryForDisplay(queryObj);

            if (queryObj.filter && queryObj.filter.query) {
              result = {
                displayData: queryObj.filter.query,
                rawData: JSON.stringify(filteredJson, null, 2),
                isJson: true,
              };
            } else {
              const formattedJson = JSON.stringify(filteredJson, null, 2);
              result = {
                displayData: formattedJson.replace(/\n/g, "").replace(/\s+/g, " "),
                rawData: formattedJson,
                isJson: true,
              };
            }
          } catch {
            result = {
              displayData: queryRaw,
              rawData: JSON.stringify(filteredInput, null, 2),
              isJson: true,
            };
          }
        } else {
          const filteredJson = filterQueryForDisplay(queryRaw);
          if (queryRaw.filter && queryRaw.filter.query) {
            result = {
              displayData: queryRaw.filter.query,
              rawData: JSON.stringify(filteredJson, null, 2),
              isJson: true,
            };
          } else {
            const formattedJson = JSON.stringify(filteredJson, null, 2);
            result = {
              displayData: formattedJson.replace(/\n/g, "").replace(/\s+/g, " "),
              rawData: formattedJson,
              isJson: true,
            };
          }
        }
        break;
      }

      case "mcp__tierzero__query_grafana_cloud_metrics": {
        const queryRaw = parsedRawInput.query;
        result = {
          displayData: queryRaw || "",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== GCP CLOUD MONITORING TOOLS =====
      case "mcp__tierzero__query_gcp_cloud_monitoring_logs": {
        const queryRaw = parsedRawInput.query;
        // GCP Cloud Logging uses filter strings, not JSON
        // If query is empty, show "All logs" to indicate no filter
        result = {
          displayData: queryRaw || "(All logs)",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_gcp_cloud_monitoring_metrics": {
        const queryRaw = parsedRawInput.query;
        // PromQL query - show placeholder if empty (though typically required)
        result = {
          displayData: queryRaw || "(No query)",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_gcp_cloud_monitoring_log_buckets": {
        // Discovery tool with no parameters - show empty JSON object
        result = {
          displayData: "(Discovering log buckets)",
          rawData: "{}",
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_gcp_cloud_monitoring_logs_aggregation": {
        const queryRaw = parsedRawInput.query;
        // BigQuery SQL query - show as SQL text (similar to CloudWatch Insights)
        if (!queryRaw) {
          result = { displayData: "", rawData: "", isJson: false };
          break;
        }
        // Display SQL query directly (use accordion format for long queries)
        result = {
          displayData: queryRaw,
          rawData: queryRaw,
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_gcp_cloud_monitoring_dashboard": {
        const dashboardId = parsedRawInput.dashboard_id;
        result = {
          displayData: `dashboard_id:${dashboardId}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== NEW RELIC TOOLS =====
      case "mcp__tierzero__query_new_relic_logs":
      case "mcp__tierzero__query_new_relic_logs_aggregation":
      case "mcp__tierzero__query_new_relic_spans":
      case "mcp__tierzero__query_new_relic_spans_aggregation":
      case "mcp__tierzero__query_new_relic_transactions":
      case "mcp__tierzero__query_new_relic_transactions_aggregation": {
        const queryRaw = parsedRawInput.query;
        if (!queryRaw) {
          result = { displayData: "", rawData: "", isJson: false };
          break;
        }

        if (typeof queryRaw === "string") {
          try {
            const queryObj = JSON.parse(queryRaw);
            const filteredJson = filterQueryForDisplay(queryObj);

            if (queryObj.filter && queryObj.filter.query) {
              result = {
                displayData: queryObj.filter.query,
                rawData: JSON.stringify(filteredJson, null, 2),
                isJson: true,
              };
            } else {
              const formattedJson = JSON.stringify(filteredJson, null, 2);
              result = {
                displayData: formattedJson.replace(/\n/g, "").replace(/\s+/g, " "),
                rawData: formattedJson,
                isJson: true,
              };
            }
          } catch {
            result = {
              displayData: queryRaw,
              rawData: JSON.stringify(filteredInput, null, 2),
              isJson: true,
            };
          }
        } else {
          const filteredJson = filterQueryForDisplay(queryRaw);
          if (queryRaw.filter && queryRaw.filter.query) {
            result = {
              displayData: queryRaw.filter.query,
              rawData: JSON.stringify(filteredJson, null, 2),
              isJson: true,
            };
          } else {
            const formattedJson = JSON.stringify(filteredJson, null, 2);
            result = {
              displayData: formattedJson.replace(/\n/g, "").replace(/\s+/g, " "),
              rawData: formattedJson,
              isJson: true,
            };
          }
        }
        break;
      }

      case "mcp__tierzero__query_new_relic_metrics": {
        const queryRaw = parsedRawInput.query;
        result = {
          displayData: queryRaw || "",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_new_relic_dashboard": {
        const dashboardIdentifier = parsedRawInput.dashboard_slug
          ? `dashboard_slug:${parsedRawInput.dashboard_slug}`
          : `dashboard_id:${parsedRawInput.dashboard_id}`;
        result = {
          displayData: dashboardIdentifier,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_new_relic_change_tracking": {
        const queryRaw = parsedRawInput.query;
        result = {
          displayData: queryRaw || "",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== DEFAULT TOOLS =====
      case "mcp__tierzero__get_relevant_context": {
        // This tool likely has a query or similar parameter
        const queryRaw = parsedRawInput.query || parsedRawInput.search_query;
        result = {
          displayData: queryRaw || "",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__save_snippet": {
        const content = parsedRawInput.content || "";
        const scope = parsedRawInput.scope || "";
        const displayParts = [content, scope].filter(Boolean);
        result = {
          displayData: displayParts.join(" | "),
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== ROOTLY TOOLS =====
      case "mcp__tierzero__query_rootly_dashboard": {
        const dashboardIdentifier = parsedRawInput.dashboard_slug
          ? `dashboard_slug:${parsedRawInput.dashboard_slug}`
          : `dashboard_id:${parsedRawInput.dashboard_id}`;
        result = {
          displayData: dashboardIdentifier,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_rootly_oncall": {
        const oncallIdentifier = parsedRawInput.user_filter
          ? `user:${parsedRawInput.user_filter}`
          : parsedRawInput.schedule_id
            ? `schedule_id:${parsedRawInput.schedule_id}`
            : "(all schedules)";
        result = {
          displayData: oncallIdentifier,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__update_rootly_incident": {
        result = {
          displayData: parsedRawInput.incident_id ? `incident:${parsedRawInput.incident_id}` : "",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== SENTRY TOOLS =====
      case "mcp__tierzero__query_sentry_issues": {
        const displayParts: string[] = [
          `project:${parsedRawInput.project_id}`,
          `env:${parsedRawInput.environment}`
        ];

        if (parsedRawInput.query_params) {
          displayParts.push(`query:${parsedRawInput.query_params}`);
        }

        result = {
          displayData: displayParts.join(" | "),
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_sentry_issue_events": {
        result = {
          displayData: `issue_id:${parsedRawInput.issue_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_sentry_trace_details": {
        result = {
          displayData: `trace_id:${parsedRawInput.trace_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_sentry_traces": {
        const displayParts: string[] = [];
        if (parsedRawInput.query) {
          displayParts.push(`query:${parsedRawInput.query}`);
        }
        displayParts.push(`period:${parsedRawInput.stats_period || "14d"}`);

        result = {
          displayData: displayParts.join(" | "),
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_sentry_events": {
        const displayParts: string[] = [`dataset:${parsedRawInput.dataset || "errors"}`];
        if (parsedRawInput.query) {
          displayParts.push(`query:${parsedRawInput.query}`);
        }
        if (parsedRawInput.start && parsedRawInput.end) {
          displayParts.push(`range:${parsedRawInput.start}→${parsedRawInput.end}`);
        } else {
          displayParts.push(`period:${parsedRawInput.stats_period || "14d"}`);
        }

        result = {
          displayData: displayParts.join(" | "),
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_sentry_events_aggregation": {
        const displayParts: string[] = [`dataset:${parsedRawInput.dataset || "errors"}`];
        if (parsedRawInput.query) {
          displayParts.push(`query:${parsedRawInput.query}`);
        }
        if (parsedRawInput.y_axis) {
          displayParts.push(`y_axis:${parsedRawInput.y_axis}`);
        }
        if (parsedRawInput.interval_seconds) {
          displayParts.push(`interval:${parsedRawInput.interval_seconds}s`);
        }
        if (parsedRawInput.group_by) {
          displayParts.push(`group_by:${parsedRawInput.group_by}`);
        }
        if (parsedRawInput.start && parsedRawInput.end) {
          displayParts.push(`range:${parsedRawInput.start}→${parsedRawInput.end}`);
        } else {
          displayParts.push(`period:${parsedRawInput.stats_period || "14d"}`);
        }

        result = {
          displayData: displayParts.join(" | "),
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== ROLLBAR TOOLS =====
      case "mcp__tierzero__query_rollbar_items": {
        const displayParts: string[] = [];
        if (parsedRawInput.status) displayParts.push(`status:${parsedRawInput.status}`);
        if (parsedRawInput.level) displayParts.push(`level:${parsedRawInput.level}`);
        if (parsedRawInput.environment) displayParts.push(`env:${parsedRawInput.environment}`);
        if (parsedRawInput.query) displayParts.push(`query:${parsedRawInput.query}`);
        result = {
          displayData: displayParts.length > 0 ? displayParts.join(" | ") : "all items",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_rollbar_item": {
        result = {
          displayData: `item_counter:${parsedRawInput.item_counter}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_rollbar_item_occurrences": {
        result = {
          displayData: `item_counter:${parsedRawInput.item_counter} | page:${parsedRawInput.page ?? 1}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_rollbar_occurrence": {
        result = {
          displayData: `occurrence_id:${parsedRawInput.occurrence_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_rollbar_project_occurrences": {
        const displayParts: string[] = [];
        if (parsedRawInput.environment) displayParts.push(`env:${parsedRawInput.environment}`);
        if (parsedRawInput.query) displayParts.push(`query:${parsedRawInput.query}`);
        result = {
          displayData: displayParts.length > 0 ? displayParts.join(" | ") : "all occurrences",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_rollbar_metrics_occurrences": {
        const displayParts: string[] = [
          `range:${parsedRawInput.start_time_unix}→${parsedRawInput.end_time_unix}`,
        ];
        if (parsedRawInput.environments?.length) {
          displayParts.push(`envs:${parsedRawInput.environments.join(",")}`);
        }
        if (parsedRawInput.levels?.length) {
          displayParts.push(`levels:${parsedRawInput.levels.join(",")}`);
        }
        if (parsedRawInput.group_by?.length) {
          displayParts.push(`group_by:${parsedRawInput.group_by.join(",")}`);
        }
        result = {
          displayData: displayParts.join(" | "),
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_rollbar_metrics_items": {
        const displayParts: string[] = [
          `range:${parsedRawInput.start_time_unix}→${parsedRawInput.end_time_unix}`,
        ];
        if (parsedRawInput.item_counters?.length) {
          displayParts.push(`items:${parsedRawInput.item_counters.join(",")}`);
        }
        if (parsedRawInput.environments?.length) {
          displayParts.push(`envs:${parsedRawInput.environments.join(",")}`);
        }
        if (parsedRawInput.levels?.length) {
          displayParts.push(`levels:${parsedRawInput.levels.join(",")}`);
        }
        result = {
          displayData: displayParts.join(" | "),
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_rollbar_top_active_items": {
        const displayParts: string[] = [`hours:${parsedRawInput.hours ?? 24}`];
        if (parsedRawInput.environments?.length) {
          displayParts.push(`envs:${parsedRawInput.environments.join(",")}`);
        }
        result = {
          displayData: displayParts.join(" | "),
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_rollbar_occurrence_counts": {
        const displayParts: string[] = [];
        if (parsedRawInput.environments?.length) {
          displayParts.push(`envs:${parsedRawInput.environments.join(",")}`);
        }
        if (parsedRawInput.min_level) displayParts.push(`min_level:${parsedRawInput.min_level}`);
        if (parsedRawInput.max_level) displayParts.push(`max_level:${parsedRawInput.max_level}`);
        result = {
          displayData: displayParts.length > 0 ? displayParts.join(" | ") : "project totals",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_rollbar_deploys": {
        const displayParts: string[] = [];
        if (parsedRawInput.environment) displayParts.push(`env:${parsedRawInput.environment}`);
        if (parsedRawInput.before) displayParts.push(`before:${parsedRawInput.before}`);
        if (parsedRawInput.after) displayParts.push(`after:${parsedRawInput.after}`);
        displayParts.push(`page:${parsedRawInput.page ?? 1}`);
        result = {
          displayData: displayParts.join(" | "),
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_rollbar_version": {
        result = {
          displayData: `version:${parsedRawInput.version} | env:${parsedRawInput.environment}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_rollbar_version_items": {
        result = {
          displayData: `version:${parsedRawInput.version} | env:${parsedRawInput.environment}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_rollbar_rql": {
        result = {
          displayData: `query:${parsedRawInput.query}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__submit_rollbar_rql": {
        result = {
          displayData: `query:${parsedRawInput.query}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_rollbar_rql_job": {
        result = {
          displayData: `job_id:${parsedRawInput.job_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_rollbar_environments": {
        result = {
          displayData: "all environments",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== SLACK TOOLS =====
      case "mcp__tierzero__query_slack_channel_contents": {
        const displayParts: string[] = [`channel:${parsedRawInput.channel_id}`];

        if (parsedRawInput.thread_ts) {
          displayParts.push(`thread:${parsedRawInput.thread_ts}`);
        }

        result = {
          displayData: displayParts.join(" | "),
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_slack_channel_metadata": {
        const displayParts: string[] = [];

        if (parsedRawInput.channel_name_filter) {
          displayParts.push(`filter:${parsedRawInput.channel_name_filter}`);
        } else {
          displayParts.push("all channels");
        }

        result = {
          displayData: displayParts.join(" | "),
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__get_mention_slack_usergroup": {
        result = {
          displayData: `usergroup:${parsedRawInput.usergroup_handle}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__get_mention_slack_user": {
        result = {
          displayData: `user:${parsedRawInput.user_name}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__create_slack_channel": {
        const visibility = parsedRawInput.is_private ? "private" : "public";
        result = {
          displayData: `${visibility} channel:${parsedRawInput.channel_name || "?"}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== TEMPORAL TOOLS =====
      case "mcp__tierzero__query_temporal_workflows": {
        result = {
          displayData: `namespace:${parsedRawInput.namespace} | query:${parsedRawInput.query}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_temporal_workflow_history": {
        result = {
          displayData: `namespace:${parsedRawInput.namespace} | workflow_id:${parsedRawInput.workflow_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== LANGSMITH TOOLS =====
      case "mcp__tierzero__query_langsmith_runs": {
        const projectDisplay = parsedRawInput.project_name || parsedRawInput.project_id || "unknown";
        result = {
          displayData: `project:${projectDisplay}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }
      case "mcp__tierzero__get_langsmith_run_stats": {
        const statsProjectDisplay = parsedRawInput.project_name || parsedRawInput.project_id || "unknown";
        result = {
          displayData: `stats project:${statsProjectDisplay}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== LANGFUSE TOOLS =====
      case "mcp__tierzero__query_langfuse_traces": {
        const nameDisplay = parsedRawInput.name || "";
        result = {
          displayData: nameDisplay ? `traces name:${nameDisplay}` : `traces from:${parsedRawInput.from_timestamp || ""}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }
      case "mcp__tierzero__get_langfuse_trace": {
        result = {
          displayData: `trace:${parsedRawInput.trace_id || "unknown"}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }
      case "mcp__tierzero__get_langfuse_metrics": {
        result = {
          displayData: `metrics view:${parsedRawInput.view || "unknown"}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== WEB SEARCH TOOLS =====
      case "mcp__tierzero__search_web": {
        const query = parsedRawInput.query || "";
        result = {
          displayData: query.length > 100 ? query.substring(0, 100) + "..." : query,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__fetch_web_content": {
        const urls = parsedRawInput.urls || [];
        let displayText = "";
        if (urls.length === 1) {
          displayText = urls[0];
        } else if (urls.length > 1) {
          displayText = `${urls.length} URLs`;
        }
        result = {
          displayData: displayText,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__run_capability": {
        const capabilityName = parsedRawInput.capability_name || "";
        const runtimeParams = parsedRawInput.runtime_params || {};
        result = {
          displayData: capabilityName || "(Running capability)",
          rawData: JSON.stringify(runtimeParams, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== PAGERDUTY TOOLS =====
      case "mcp__tierzero__query_pagerduty_incidents": {
        const displayParts: string[] = [];
        if (parsedRawInput.query) displayParts.push(`query:${parsedRawInput.query}`);
        if (parsedRawInput.statuses) displayParts.push(`statuses:${parsedRawInput.statuses}`);
        result = {
          displayData: displayParts.length > 0 ? displayParts.join(" | ") : "(all incidents)",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_pagerduty_incident": {
        result = {
          displayData: `incident_id:${parsedRawInput.incident_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_pagerduty_incident_alerts": {
        result = {
          displayData: `incident_id:${parsedRawInput.incident_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_pagerduty_incident_notes": {
        result = {
          displayData: `incident_id:${parsedRawInput.incident_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== FIREHYDRANT TOOLS =====
      case "mcp__tierzero__query_firehydrant_incidents": {
        const displayParts: string[] = [];
        if (parsedRawInput.query) displayParts.push(`query:${parsedRawInput.query}`);
        result = {
          displayData: displayParts.length > 0 ? displayParts.join(" | ") : "(all incidents)",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_firehydrant_incident":
      case "mcp__tierzero__query_firehydrant_incident_alerts":
      case "mcp__tierzero__query_firehydrant_incident_external_resources":
      case "mcp__tierzero__query_firehydrant_incident_bridge_summaries": {
        result = {
          displayData: `incident_id:${parsedRawInput.incident_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_firehydrant_alerts": {
        result = {
          displayData: "(all alerts)",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_firehydrant_alert": {
        result = {
          displayData: `alert_id:${parsedRawInput.alert_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_firehydrant_retrospectives":
      case "mcp__tierzero__query_firehydrant_post_mortem_reports": {
        result = {
          displayData: `report_id:${parsedRawInput.report_id || parsedRawInput.retrospective_id || ""}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== INCIDENT.IO TOOLS =====
      case "mcp__tierzero__query_incident_io_incidents": {
        const displayParts: string[] = [];
        if (parsedRawInput.query) displayParts.push(`query:${parsedRawInput.query}`);
        result = {
          displayData: displayParts.length > 0 ? displayParts.join(" | ") : "(all incidents)",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_incident_io_incident":
      case "mcp__tierzero__query_incident_io_incident_alerts": {
        result = {
          displayData: `incident_id:${parsedRawInput.incident_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_incident_io_alert": {
        result = {
          displayData: `alert_id:${parsedRawInput.alert_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== CLI TOOLS =====
      case "mcp__tierzero__RunCliCmd": {
        const baseCmd = parsedRawInput.base_cmd || "aws";
        const args = parsedRawInput.args || "";
        result = {
          displayData: `${baseCmd} ${args}`.trim(),
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== JIRA TOOLS =====
      case "mcp__tierzero__get_jira_issue": {
        result = {
          displayData: `issue_key:${parsedRawInput.issue_key}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__search_jira_issues": {
        result = {
          displayData: `jql:${parsedRawInput.jql}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__create_jira_issue": {
        const createDisplay = parsedRawInput.summary
          ? `project:${parsedRawInput.project_key || "?"} summary:${parsedRawInput.summary}`
          : "";
        result = {
          displayData: createDisplay,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__update_jira_issue": {
        result = {
          displayData: parsedRawInput.issue_key ? `issue_key:${parsedRawInput.issue_key}` : "",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__add_jira_comment": {
        result = {
          displayData: parsedRawInput.issue_key ? `issue_key:${parsedRawInput.issue_key}` : "",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__edit_jira_comment": {
        const parts: string[] = [];
        if (parsedRawInput.issue_key) parts.push(`issue_key:${parsedRawInput.issue_key}`);
        if (parsedRawInput.comment_id) parts.push(`comment_id:${parsedRawInput.comment_id}`);
        result = {
          displayData: parts.join(" "),
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__list_jira_projects": {
        result = {
          displayData: "listing projects",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== LINEAR TOOLS =====
      case "mcp__tierzero__search_linear_issues": {
        const parts: string[] = [];
        if (parsedRawInput.query) parts.push(`query:${parsedRawInput.query}`);
        if (parsedRawInput.team_id) parts.push(`team:${parsedRawInput.team_id}`);
        if (parsedRawInput.status) parts.push(`status:${parsedRawInput.status}`);
        result = {
          displayData: parts.length > 0 ? parts.join(" ") : "all issues",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__get_linear_issue": {
        result = {
          displayData: `issue_id:${parsedRawInput.issue_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__create_linear_issue": {
        const createDisplay = parsedRawInput.title
          ? `team:${parsedRawInput.team_id || "?"} title:${parsedRawInput.title}`
          : "";
        result = {
          displayData: createDisplay,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__update_linear_issue": {
        result = {
          displayData: parsedRawInput.issue_id ? `issue_id:${parsedRawInput.issue_id}` : "",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__add_linear_comment": {
        result = {
          displayData: parsedRawInput.issue_id ? `issue_id:${parsedRawInput.issue_id}` : "",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__edit_linear_comment": {
        result = {
          displayData: parsedRawInput.comment_id ? `comment_id:${parsedRawInput.comment_id}` : "",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__list_linear_teams": {
        result = {
          displayData: "listing teams",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__list_linear_users": {
        const userParts: string[] = [];
        if (parsedRawInput.app_only) userParts.push("agents only");
        if (parsedRawInput.include_disabled) userParts.push("incl. disabled");
        result = {
          displayData: userParts.length > 0 ? userParts.join(", ") : "listing users",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__list_linear_projects": {
        const listParts: string[] = [];
        if (parsedRawInput.team_id) listParts.push(`team:${parsedRawInput.team_id}`);
        if (parsedRawInput.label_name) listParts.push(`label:${parsedRawInput.label_name}`);
        result = {
          displayData: listParts.length > 0 ? listParts.join(" ") : "all projects",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__search_linear_projects": {
        const searchParts: string[] = [];
        if (parsedRawInput.term) searchParts.push(`term:${parsedRawInput.term}`);
        if (parsedRawInput.team_id) searchParts.push(`team:${parsedRawInput.team_id}`);
        result = {
          displayData: searchParts.length > 0 ? searchParts.join(" ") : "",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__get_linear_project": {
        result = {
          displayData: parsedRawInput.project_id ? `project_id:${parsedRawInput.project_id}` : "",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__create_linear_project": {
        result = {
          displayData: parsedRawInput.name ? `name:${parsedRawInput.name}` : "",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__update_linear_project": {
        result = {
          displayData: parsedRawInput.project_id ? `project_id:${parsedRawInput.project_id}` : "",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== CUSTOM MCP GATEWAY TOOLS =====
      case "mcp__tierzero__list_custom_mcp_server_tools": {
        result = {
          displayData: "list_tools",
          rawData: "list_tools",
          isJson: false,
        };
        break;
      }

      case "mcp__tierzero__call_custom_mcp_server_tool": {
        const display = parsedRawInput.tool_name || "call_tool";
        result = {
          displayData: display,
          rawData: parsedRawInput.arguments || display,
          isJson: !!parsedRawInput.arguments,
        };
        break;
      }

      // ===== SOCKET TOOLS =====
      case "mcp__tierzero__query_socket_alerts": {
        const displayParts: string[] = [];
        if (parsedRawInput.repo_slug) displayParts.push(`repo:${parsedRawInput.repo_slug}`);
        if (parsedRawInput.reachability_type) displayParts.push(`reachability:${parsedRawInput.reachability_type}`);
        if (parsedRawInput.severity) displayParts.push(`severity:${parsedRawInput.severity}`);
        if (parsedRawInput.category) displayParts.push(`category:${parsedRawInput.category}`);
        result = {
          displayData: displayParts.length > 0 ? displayParts.join(" | ") : "(all alerts)",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__list_socket_repos": {
        result = {
          displayData: "(all repos)",
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      case "mcp__tierzero__query_socket_scan": {
        result = {
          displayData: `scan_id:${parsedRawInput.scan_id}`,
          rawData: JSON.stringify(filteredInput, null, 2),
          isJson: true,
        };
        break;
      }

      // ===== DEFAULT CASE =====
      default: {
        // Fallback for unknown tools - try to display something reasonable
        const queryRaw = parsedRawInput.query;
        if (queryRaw) {
          if (typeof queryRaw === "string") {
            try {
              const queryObj = JSON.parse(queryRaw);
              const filteredJson = filterQueryForDisplay(queryObj);

              if (queryObj.filter && queryObj.filter.query) {
                result = {
                  displayData: queryObj.filter.query,
                  rawData: JSON.stringify(filteredJson, null, 2),
                  isJson: true,
                };
              } else {
                const formattedJson = JSON.stringify(filteredJson, null, 2);
                result = {
                  displayData: formattedJson.replace(/\n/g, "").replace(/\s+/g, " "),
                  rawData: formattedJson,
                  isJson: true,
                };
              }
            } catch {
              result = {
                displayData: queryRaw,
                rawData: JSON.stringify(filteredInput, null, 2),
                isJson: true,
              };
            }
          } else {
            const filteredJson = filterQueryForDisplay(queryRaw);
            const formattedJson = JSON.stringify(filteredJson, null, 2);
            result = {
              displayData: formattedJson.replace(/\n/g, "").replace(/\s+/g, " "),
              rawData: formattedJson,
              isJson: true,
            };
          }
        } else {
          // No query field, show the entire input
          const formattedJson = JSON.stringify(filteredInput, null, 2);
          result = {
            displayData: formattedJson.replace(/\n/g, "").replace(/\s+/g, " "),
            rawData: formattedJson,
            isJson: true,
          };
        }
        break;
      }
    }

    // Truncate displayData to 100 characters
    if (result.displayData && result.displayData.length > 100) {
      result.displayData = result.displayData.substring(0, 100) + "...";
    }

    return result;
  } catch (error) {
    console.error("Error parsing V2 rawInput:", error);
    return { displayData: "", rawData: "", isJson: false };
  }
}

/**
 * Extract time range from V2 rawInput
 */
export function extractV2TimeRange(rawInput: any): { startTime?: string; endTime?: string } {
  try {
    const parsedRawInput = typeof rawInput === "string" ? JSON.parse(rawInput) : rawInput;
    return {
      startTime: parsedRawInput.start_iso_str,
      endTime: parsedRawInput.end_iso_str,
    };
  } catch (error) {
    console.error("Error extracting V2 time range:", error);
    return { startTime: undefined, endTime: undefined };
  }
}

/**
 * Filter query object to remove internal fields and null values
 */
function filterQueryForDisplay(query: any): any {
  if (query === null || query === undefined) {
    return null;
  }

  if (Array.isArray(query)) {
    const filtered = query.map(filterQueryForDisplay).filter(item => item !== null && item !== undefined);
    return filtered.length > 0 ? filtered : null;
  }

  if (typeof query === "object") {
    const filtered: any = {};
    for (const [key, value] of Object.entries(query)) {
      // Skip __typename, question, internal fields starting with underscore, time range fields, and integration_id
      if (
        key === "__typename" ||
        key === "question" ||
        key.startsWith("_") ||
        key === "start_iso_str" ||
        key === "end_iso_str" ||
        key === "integration_id"
      ) {
        continue;
      }
      // Skip null/undefined values
      if (value === null || value === undefined) {
        continue;
      }
      // Recursively filter nested objects/arrays
      const filteredValue = filterQueryForDisplay(value);
      if (filteredValue !== null && filteredValue !== undefined) {
        filtered[key] = filteredValue;
      }
    }
    return Object.keys(filtered).length > 0 ? filtered : null;
  }

  return query;
}

/**
 * Safely stringify a filtered query object, handling null/undefined results
 * Returns an empty object string "{}" if the input is null/undefined
 */
function safeStringifyFiltered(filtered: any): string {
  return JSON.stringify(filtered ?? {}, null, 2);
}

/**
 * Process the tool result content to clean it up for display
 * This is needed because the backend is not returning in JSON (yet)
 */
export function processToolContent(toolCall?: any): string {
  const toolResult = toolCall.toolResult
  if (!toolResult || !toolResult.content) return "";
  let content = toolResult.content;

  try {
    // Remove the Tool Call ID section header if it exists (including the entire line)
    content = content.replace(/(#{1,3})\s*Tool Call ID.*\ncall_.*\n/, "");
    content = content.replace(/(#{1,3})\s*Tool Call ID.*\ntoolu_.*\n/, "");
    content = content.replace(/(#{1,3})\s*Tool Call ID.*\ntool_.*\n/, "");
    content = content.replace(/(#{1,3})\s*Tool Call ID.*\nartifact_[\w-]+\.json\n/, "");

    // Remove the Answer header if it exists
    content = content.replace(/(#{1,3})\s*Answer\s*\n/, "");

    // Replace • with bullet point for markdown display
    content = content.replace(/•/g, "-");

    // Note: UpdateTodoTool is an internal tool not exposed in GraphQL
    // Todo list formatting is handled in ChecklistToolCallResult component
    content = content.trim()

    // Remove any leading #
    content = content.replace(/^#+/, "");
  } catch {
    // If any error occurs during processing, just return the original content
  }

  return content;
}

/**
 * Safely parse JSON string, returning null on error
 */
function safelyParseJson(value: string): any {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/**
 * Parse todos from TodoWrite tool input (rawInput field)
 */
export function parseTodoWriteInput(input: any): any[] | null {
  if (!input?.rawInput) {
    return null;
  }

  // Handle rawInput as either JSON string or object (consistent with other V2 tools)
  const rawInput =
    typeof input.rawInput === "string" ? safelyParseJson(input.rawInput) : input.rawInput;

  // TodoWrite input has a "todos" array with objects containing: content, status, activeForm
  if (rawInput?.todos && Array.isArray(rawInput.todos)) {
    return rawInput.todos.map((todo: any, index: number) => ({
      task_num: index + 1,
      title: todo.content || "",
      status: (todo.status ?? "PENDING").toString().toUpperCase(),
      description: ""
    }));
  }

  return null;
}

export function parseCompleteTodoList(toolResult?: any): any[] | null {
  if (!toolResult?.content) return null;

  const content = toolResult.content;

  // Look for the "Current To-Do List Status" section
  // Extract everything after the header until we hit another ## header (but be more careful about the boundary)
  const todoSectionMatch = content.match(/## Current To-Do List Status\s*([\s\S]*?)(?=\n##(?!\s*\[)|$)/);
  if (!todoSectionMatch) return null;

  // Parse tasks directly from the entire content, not just the section
  // This is more robust as it finds all tasks regardless of section boundaries
  const taskRegex = /### \[([^\]]+)\] (\d+): ([^\n]+)/g;
  const tasks = [];
  let match;

  // Search the entire content to be sure we don't miss any tasks
  while ((match = taskRegex.exec(content)) !== null) {
    const [, status, taskNum, title] = match;
    tasks.push({
      task_num: parseInt(taskNum, 10),
      title: title.trim(),
      status: status.trim(),
      description: "" // Description is not included in the v2 format result
    });
  }

  return tasks.length > 0 ? tasks : null;
}

/**
 * Process query data for display in the query accordion
 */
export function processQueryData(toolUseInput?: any, toolName?: string): ProcessedQueryData {
  if (!toolUseInput) return { displayData: "", rawData: "", isJson: false };

  // Check if all values in toolUseInput are null or undefined
  if (Object.values(toolUseInput).every(value => value === null || value === undefined)) {
    return { displayData: "", rawData: "", isJson: false };
  }

  // Special handling for QueryDatadogErrorTrackingIssuesTool - show query in header, full JSON in expanded
  if (toolName === "QueryDatadogErrorTrackingIssuesTool") {
    const filteredInput = filterQueryForDisplay(toolUseInput);
    const formattedJson = safeStringifyFiltered(filteredInput);
    return {
      displayData: toolUseInput.query || formattedJson,
      rawData: formattedJson,
      isJson: true,
    };
  }

  // Handle typed query fields (query, queryJson)
  const queryRaw = "query" in toolUseInput ? toolUseInput.query :
                   "queryJson" in toolUseInput ? toolUseInput.queryJson : undefined;

  if (queryRaw) {
    try {
      // Try to parse it as JSON for display
      let parsedJson;
      if (typeof queryRaw === "object") {
        parsedJson = queryRaw;
      } else if (typeof queryRaw === "string") {
        parsedJson = JSON.parse(queryRaw);
      }

      // Filter out __typename, question, and null values
      const filteredJson = filterQueryForDisplay(parsedJson);

      // Check if it has filter.query which should be shown in the dropdown
      if (parsedJson.filter && parsedJson.filter.query) {
        return {
          displayData: parsedJson.filter.query,
          rawData: safeStringifyFiltered(filteredJson),
          isJson: true,
        };
      }

      // No filter.query, use the full JSON
      const formattedJson = safeStringifyFiltered(filteredJson);
      return {
        displayData: formattedJson.replace(/\n/g, "").replace(/\s+/g, ""),
        rawData: formattedJson,
        isJson: true,
      };
    } catch {
      // If it's not valid JSON, show as plain text
      return {
        displayData: queryRaw,
        rawData: queryRaw,
        isJson: false,
      };
    }
  }

  // Handle Buildkite tools
  if ("pipelineSlug" in toolUseInput) {
    const parts = [`pipelineSlug:${toolUseInput.pipelineSlug}`];

    // Handle both string and int build numbers (field aliases)
    if ("buildNumberStr" in toolUseInput && toolUseInput.buildNumberStr) {
      parts.push(`buildNumberStr:${toolUseInput.buildNumberStr}`);
    } else if ("buildNumberInt" in toolUseInput) {
      parts.push(`buildNumberInt:${toolUseInput.buildNumberInt}`);
    }

    const filteredInput = filterQueryForDisplay(toolUseInput);
    const formattedInput = safeStringifyFiltered(filteredInput);
    return {
      displayData: parts.join(" "),
      rawData: formattedInput,
      isJson: true,
    };
  }

  if ("testSuiteSlug" in toolUseInput) {
    const filteredInput = filterQueryForDisplay(toolUseInput);
    const formattedInput = safeStringifyFiltered(filteredInput);
    return {
      displayData: `testSuiteSlug:${toolUseInput.testSuiteSlug}`,
      rawData: formattedInput,
      isJson: true,
    };
  }

  // Handle Slack tools
  if ("channelId" in toolUseInput) {
    const parts = [`channelId:${toolUseInput.channelId}`];
    if ("threadTs" in toolUseInput && toolUseInput.threadTs) {
      parts.push(`threadTs:${toolUseInput.threadTs}`);
    }
    const filteredInput = filterQueryForDisplay(toolUseInput);
    const formattedInput = safeStringifyFiltered(filteredInput);
    return {
      displayData: parts.join(" "),
      rawData: formattedInput,
      isJson: true,
    };
  }

  if ("channelNameFilter" in toolUseInput) {
    const filteredInput = filterQueryForDisplay(toolUseInput);
    const formattedInput = safeStringifyFiltered(filteredInput);
    return {
      displayData: `channelNameFilter:${toolUseInput.channelNameFilter}`,
      rawData: formattedInput,
      isJson: true,
    };
  }

  if ("usergroupHandle" in toolUseInput) {
    const filteredInput = filterQueryForDisplay(toolUseInput);
    const formattedInput = safeStringifyFiltered(filteredInput);
    return {
      displayData: `usergroupHandle:${toolUseInput.usergroupHandle}`,
      rawData: formattedInput,
      isJson: true,
    };
  }

  // Handle GitHub displayGithubCommitsQueryStr
  if ("displayGithubCommitsQueryStr" in toolUseInput) {
    return {
      displayData: toolUseInput.displayGithubCommitsQueryStr,
      rawData: "",
      isJson: false,
    };
  }

  // Handle GitHub code tool (just repo)
  if ("repoName" in toolUseInput && !("runId" in toolUseInput)) {
    const filteredInput = filterQueryForDisplay(toolUseInput);
    const formattedInput = safeStringifyFiltered(filteredInput);
    return {
      displayData: `repoName:${toolUseInput.repoName}`,
      rawData: formattedInput,
      isJson: true,
    };
  }

  // Handle GitHub Actions runs
  if ("repoName" in toolUseInput && "runId" in toolUseInput) {
    const filteredInput = filterQueryForDisplay(toolUseInput);
    const formattedInput = safeStringifyFiltered(filteredInput);
    return {
      displayData: `repoName:${toolUseInput.repoName} runId:${toolUseInput.runId}`,
      rawData: formattedInput,
      isJson: true,
    };
  }

  // Handle Sentry Issues tool
  if ("projectId" in toolUseInput && "environment" in toolUseInput) {
    const parts = [
      `projectId:${toolUseInput.projectId}`,
      `environment:${toolUseInput.environment}`
    ];
    // Note: Field is aliased as sentryQueryParams in GraphQL fragment
    if ("sentryQueryParams" in toolUseInput && toolUseInput.sentryQueryParams) {
      parts.push(`queryParams:${toolUseInput.sentryQueryParams}`);
    }
    const filteredInput = filterQueryForDisplay(toolUseInput);
    const formattedInput = safeStringifyFiltered(filteredInput);
    return {
      displayData: parts.join(" "),
      rawData: formattedInput,
      isJson: true,
    };
  }

  // Handle Sentry Issue Events tool
  if ("issueId" in toolUseInput) {
    const filteredInput = filterQueryForDisplay(toolUseInput);
    const formattedInput = safeStringifyFiltered(filteredInput);
    return {
      displayData: `issueId:${toolUseInput.issueId}`,
      rawData: formattedInput,
      isJson: true,
    };
  }

  // Handle Datadog Dashboard tool - check for __typename to distinguish from Rootly
  // Field is aliased as ddDashboardId in GraphQL fragment
  if (toolUseInput.__typename === "DatadogDashboardToolInput" && "ddDashboardId" in toolUseInput) {
    const filteredInput = filterQueryForDisplay(toolUseInput);

    // Transform ddDashboardId back to dashboardId for display
    if (filteredInput && "ddDashboardId" in filteredInput) {
      filteredInput.dashboardId = filteredInput.ddDashboardId;
      delete filteredInput.ddDashboardId;
    }

    const formattedInput = safeStringifyFiltered(filteredInput);
    return {
      displayData: `dashboardId:${toolUseInput.ddDashboardId}`,
      rawData: formattedInput,
      isJson: true,
    };
  }

  // Handle Datadog Notebook tool
  if (toolUseInput.__typename === "DatadogNotebookToolInput" && "notebookId" in toolUseInput) {
    const filteredInput = filterQueryForDisplay(toolUseInput);
    const formattedInput = safeStringifyFiltered(filteredInput);
    return {
      displayData: `notebookId:${toolUseInput.notebookId}`,
      rawData: formattedInput,
      isJson: true,
    };
  }

  // Handle NewRelic Dashboard tool - check for __typename to distinguish from Rootly
  // Field is aliased as nrDashboardId in GraphQL fragment
  if (toolUseInput.__typename === "NewRelicDashboardToolInput" && "nrDashboardId" in toolUseInput) {
    const filteredInput = filterQueryForDisplay(toolUseInput);

    // Transform nrDashboardId back to dashboardId for display
    if (filteredInput && "nrDashboardId" in filteredInput) {
      filteredInput.dashboardId = filteredInput.nrDashboardId;
      delete filteredInput.nrDashboardId;
    }

    const formattedInput = safeStringifyFiltered(filteredInput);
    return {
      displayData: `dashboardId:${toolUseInput.nrDashboardId}`,
      rawData: formattedInput,
      isJson: true,
    };
  }

  // Handle Rootly Dashboard tool
  if ("dashboardId" in toolUseInput || "dashboardSlug" in toolUseInput) {
    const dashboardIdentifier = toolUseInput.dashboardSlug || toolUseInput.dashboardId;
    const filteredInput = filterQueryForDisplay(toolUseInput);
    const formattedInput = safeStringifyFiltered(filteredInput);
    return {
      displayData: `dashboard:${dashboardIdentifier}`,
      rawData: formattedInput,
      isJson: true,
    };
  }

  if (toolUseInput.text) {
    const textPreview = toolUseInput.text.substring(0, 50);
    const displayText = toolUseInput.text.length > 50 ? `${textPreview}...` : textPreview;
    return {
      displayData: `Count items in list (${displayText})`,
      rawData: toolUseInput.text,
      isJson: false,
    };
  }

  // Handle SearchWebTool
  if ("userQuery" in toolUseInput && "domainFilter" in toolUseInput) {
    const parts = [
      `domain: ${toolUseInput.domainFilter}`,
      `query: "${toolUseInput.userQuery}"`
    ];
    if ("numResults" in toolUseInput) {
      parts.push(`results: ${toolUseInput.numResults}`);
    }
    const filteredInput = filterQueryForDisplay(toolUseInput);
    const formattedInput = safeStringifyFiltered(filteredInput);
    return {
      displayData: parts.join(" | "),
      rawData: formattedInput,
      isJson: true,
    };
  }

  // Handle FetchWebContentTool
  if ("urls" in toolUseInput && Array.isArray(toolUseInput.urls)) {
    const urlCount = toolUseInput.urls.length;
    const displayText = `Fetching ${urlCount} URL(s)`;
    const filteredInput = filterQueryForDisplay(toolUseInput);
    const formattedInput = safeStringifyFiltered(filteredInput);
    return {
      displayData: displayText,
      rawData: formattedInput,
      isJson: true,
    };
  }

  // Default: stringify the entire input
  const filteredInput = filterQueryForDisplay(toolUseInput);
  const formattedInput = safeStringifyFiltered(filteredInput);
  return {
    displayData: formattedInput,
    rawData: formattedInput,
    isJson: true
  };
}

/**
 * Determine if tool has results
 */
export function hasToolResults(toolCall?: ToolCall): boolean {
  return Boolean(toolCall?.toolResult && toolCall.toolResult.content);
}

/**
 * Extract tool name from a tool call and return display name if available
 */
export function getToolName(toolCall?: ToolCall): string {
  // If displayName is explicitly set, use it
  if (toolCall?.toolUse?.displayName) {
    return toolCall.toolUse.displayName;
  }

  const toolName = toolCall?.toolUse?.name || "Tool";

  // For V2 MCP tools, generate a friendly display name
  if (toolName.startsWith("mcp__tierzero__")) {
    // Map common MCP tool names to friendly names
    const mcpToolNameMap: Record<string, string> = {
      "mcp__tierzero__get_relevant_context": "GetRelevantContext",
      "mcp__tierzero__query_datadog_logs": "QueryDatadogLogs",
      "mcp__tierzero__query_datadog_logs_aggregation": "QueryDatadogLogsAggregation",
      "mcp__tierzero__query_datadog_spans": "QueryDatadogSpans",
      "mcp__tierzero__query_datadog_spans_aggregation": "QueryDatadogSpansAggregation",
      "mcp__tierzero__query_datadog_ci_pipeline_events": "QueryDatadogCIPipelineEvents",
      "mcp__tierzero__query_datadog_ci_pipeline_events_aggregation": "QueryDatadogCIPipelineEventsAggregation",
      "mcp__tierzero__query_datadog_rum_aggregation": "QueryDatadogRUMAggregation",
      "mcp__tierzero__query_datadog_events": "QueryDatadogEvents",
      "mcp__tierzero__query_datadog_error_tracking_issue_details": "QueryDatadogErrorTrackingIssueDetails",
      "mcp__tierzero__query_datadog_error_tracking_issues": "QueryDatadogErrorTrackingIssues",
      "mcp__tierzero__query_datadog_dashboard": "QueryDatadogDashboard",
      "mcp__tierzero__query_datadog_notebook": "QueryDatadogNotebook",
      "mcp__tierzero__search_datadog_notebooks": "SearchDatadogNotebooks",
      "mcp__tierzero__query_datadog_monitor": "QueryDatadogMonitor",
      "mcp__tierzero__query_datadog_incident": "QueryDatadogIncident",
      "mcp__tierzero__search_datadog_synthetic_tests": "SearchDatadogSyntheticTests",
      "mcp__tierzero__query_datadog_synthetic_test": "QueryDatadogSyntheticTest",
      "mcp__tierzero__query_datadog_synthetic_test_results": "QueryDatadogSyntheticTestResults",
      "mcp__tierzero__query_datadog_synthetic_test_result": "QueryDatadogSyntheticTestResult",
      "mcp__tierzero__query_datadog_synthetic_ci_batch": "QueryDatadogSyntheticCIBatch",
      "mcp__tierzero__query_datadog_service_dependency": "QueryDatadogServiceDependency",
      "mcp__tierzero__query_datadog_metrics": "QueryDatadogMetrics",
      "mcp__tierzero__search_datadog_metrics": "SearchDatadogMetrics",
      "mcp__tierzero__query_datadog_metric_tags": "QueryDatadogMetricTags",
      "mcp__tierzero__query_buildkite_builds": "QueryBuildkiteBuilds",
      "mcp__tierzero__query_buildkite_logs": "QueryBuildkiteLogs",
      "mcp__tierzero__query_buildkite_annotations": "QueryBuildkiteAnnotations",
      "mcp__tierzero__query_buildkite_artifacts": "QueryBuildkiteArtifacts",
      "mcp__tierzero__query_buildkite_flaky_test": "QueryBuildkiteFlakyTest",
      "mcp__tierzero__query_cloudwatch_logs": "QueryCloudWatchLogs",
      "mcp__tierzero__query_cloudwatch_logs_aggregation": "QueryCloudWatchLogsAggregation",
      "mcp__tierzero__query_cloudwatch_metrics": "QueryCloudWatchMetrics",
      "mcp__tierzero__query_grafana_cloud_logs": "QueryGrafanaLogs",
      "mcp__tierzero__query_grafana_cloud_logs_aggregation": "QueryGrafanaLogsAggregation",
      "mcp__tierzero__query_grafana_cloud_metrics": "QueryGrafanaMetrics",
      "mcp__tierzero__query_grafana_cloud_spans": "QueryGrafanaSpans",
      "mcp__tierzero__query_grafana_cloud_spans_aggregation": "QueryGrafanaSpansAggregation",
      "mcp__tierzero__query_gcp_cloud_monitoring_logs": "QueryGCPCloudMonitoringLogs",
      "mcp__tierzero__query_gcp_cloud_monitoring_metrics": "QueryGCPCloudMonitoringMetrics",
      "mcp__tierzero__query_gcp_cloud_monitoring_log_buckets": "QueryGCPCloudMonitoringLogBuckets",
      "mcp__tierzero__query_gcp_cloud_monitoring_logs_aggregation": "QueryGCPCloudMonitoringLogsAggregation",
      "mcp__tierzero__query_gcp_cloud_monitoring_dashboard": "QueryGCPCloudMonitoringDashboard",
      "mcp__tierzero__query_new_relic_logs": "QueryNewRelicLogs",
      "mcp__tierzero__query_new_relic_logs_aggregation": "QueryNewRelicLogsAggregation",
      "mcp__tierzero__query_new_relic_metrics": "QueryNewRelicMetrics",
      "mcp__tierzero__query_new_relic_spans": "QueryNewRelicSpans",
      "mcp__tierzero__query_new_relic_spans_aggregation": "QueryNewRelicSpansAggregation",
      "mcp__tierzero__query_new_relic_transactions": "QueryNewRelicTransactions",
      "mcp__tierzero__query_new_relic_transactions_aggregation": "QueryNewRelicTransactionsAggregation",
      "mcp__tierzero__query_new_relic_dashboard": "QueryNewRelicDashboard",
      "mcp__tierzero__query_new_relic_change_tracking": "QueryNewRelicChangeTracking",
      "mcp__tierzero__query_rootly_dashboard": "QueryRootlyDashboard",
      "mcp__tierzero__query_rootly_incidents": "QueryRootlyIncidents",
      "mcp__tierzero__query_rootly_incident": "QueryRootlyIncident",
      "mcp__tierzero__query_rootly_alerts": "QueryRootlyAlerts",
      "mcp__tierzero__query_rootly_alert": "QueryRootlyAlert",
      "mcp__tierzero__query_rootly_incident_alerts": "QueryRootlyIncidentAlerts",
      "mcp__tierzero__query_rootly_oncall": "QueryRootlyOncall",
      "mcp__tierzero__update_rootly_incident": "UpdateRootlyIncident",
      "mcp__tierzero__query_pagerduty_incidents": "QueryPagerDutyIncidents",
      "mcp__tierzero__query_pagerduty_incident": "QueryPagerDutyIncident",
      "mcp__tierzero__query_pagerduty_incident_alerts": "QueryPagerDutyIncidentAlerts",
      "mcp__tierzero__query_pagerduty_incident_notes": "QueryPagerDutyIncidentNotes",
      "mcp__tierzero__query_firehydrant_incidents": "QueryFireHydrantIncidents",
      "mcp__tierzero__query_firehydrant_incident": "QueryFireHydrantIncident",
      "mcp__tierzero__query_firehydrant_incident_alerts": "QueryFireHydrantIncidentAlerts",
      "mcp__tierzero__query_firehydrant_incident_external_resources": "QueryFireHydrantIncidentExternalResources",
      "mcp__tierzero__query_firehydrant_incident_bridge_summaries": "QueryFireHydrantIncidentBridgeSummaries",
      "mcp__tierzero__query_firehydrant_alerts": "QueryFireHydrantAlerts",
      "mcp__tierzero__query_firehydrant_alert": "QueryFireHydrantAlert",
      "mcp__tierzero__query_firehydrant_retrospectives": "QueryFireHydrantRetrospectives",
      "mcp__tierzero__query_firehydrant_post_mortem_reports": "QueryFireHydrantPostMortemReports",
      "mcp__tierzero__query_incident_io_incidents": "QueryIncidentIoIncidents",
      "mcp__tierzero__query_incident_io_incident": "QueryIncidentIoIncident",
      "mcp__tierzero__query_incident_io_incident_alerts": "QueryIncidentIoIncidentAlerts",
      "mcp__tierzero__query_incident_io_alert": "QueryIncidentIoAlert",
      "mcp__tierzero__search_elasticsearch_logs": "SearchElasticsearchLogs",
      "mcp__tierzero__aggregate_elasticsearch_logs": "AggregateElasticsearchLogs",
      "mcp__tierzero__list_elasticsearch_indices": "ListElasticsearchIndices",
      "mcp__tierzero__query_prometheus_metrics": "QueryPrometheusMetrics",
      "mcp__tierzero__list_prometheus_label_values": "ListPrometheusLabelValues",
      "mcp__tierzero__list_prometheus_targets": "ListPrometheusTargets",
      "mcp__tierzero__query_sentry_issues": "QuerySentryIssues",
      "mcp__tierzero__query_sentry_issue_events": "QuerySentryIssueEvents",
      "mcp__tierzero__query_sentry_trace_details": "QuerySentryTraceDetails",
      "mcp__tierzero__query_sentry_traces": "QuerySentryTraces",
      "mcp__tierzero__query_sentry_events": "QuerySentryEvents",
      "mcp__tierzero__query_sentry_events_aggregation": "QuerySentryEventsAggregation",
      "mcp__tierzero__query_rollbar_items": "QueryRollbarItems",
      "mcp__tierzero__query_rollbar_item": "QueryRollbarItem",
      "mcp__tierzero__query_rollbar_item_occurrences": "QueryRollbarItemOccurrences",
      "mcp__tierzero__query_rollbar_occurrence": "QueryRollbarOccurrence",
      "mcp__tierzero__query_rollbar_project_occurrences": "QueryRollbarProjectOccurrences",
      "mcp__tierzero__query_rollbar_metrics_occurrences": "QueryRollbarMetricsOccurrences",
      "mcp__tierzero__query_rollbar_metrics_items": "QueryRollbarMetricsItems",
      "mcp__tierzero__query_rollbar_top_active_items": "QueryRollbarTopActiveItems",
      "mcp__tierzero__query_rollbar_occurrence_counts": "QueryRollbarOccurrenceCounts",
      "mcp__tierzero__query_rollbar_deploys": "QueryRollbarDeploys",
      "mcp__tierzero__query_rollbar_version": "QueryRollbarVersion",
      "mcp__tierzero__query_rollbar_version_items": "QueryRollbarVersionItems",
      "mcp__tierzero__query_rollbar_rql": "QueryRollbarRQL",
      "mcp__tierzero__submit_rollbar_rql": "SubmitRollbarRQL",
      "mcp__tierzero__query_rollbar_rql_job": "QueryRollbarRQLJob",
      "mcp__tierzero__query_rollbar_environments": "QueryRollbarEnvironments",
      "mcp__tierzero__query_slack_channel_contents": "QuerySlackChannelContents",
      "mcp__tierzero__query_slack_channel_metadata": "QuerySlackChannelMetadata",
      "mcp__tierzero__get_mention_slack_usergroup": "GetSlackUsergroupMention",
      "mcp__tierzero__get_mention_slack_user": "GetSlackUserMention",
      "mcp__tierzero__post_slack_message": "PostSlackMessage",
      "mcp__tierzero__forward_slack_message": "ForwardSlackMessage",
      "mcp__tierzero__create_slack_channel": "CreateSlackChannel",
      "mcp__tierzero__query_temporal_workflows": "QueryTemporalWorkflows",
      "mcp__tierzero__query_temporal_workflow_history": "QueryTemporalWorkflowHistory",
      "mcp__tierzero__query_langsmith_runs": "QueryLangSmithRuns",
      "mcp__tierzero__get_langsmith_run_stats": "GetLangSmithRunStats",
      "mcp__tierzero__query_langfuse_traces": "QueryLangfuseTraces",
      "mcp__tierzero__get_langfuse_trace": "GetLangfuseTrace",
      "mcp__tierzero__get_langfuse_metrics": "GetLangfuseMetrics",
      "mcp__tierzero__get_jira_issue": "GetJiraIssue",
      "mcp__tierzero__search_jira_issues": "SearchJiraIssues",
      "mcp__tierzero__create_jira_issue": "CreateJiraIssue",
      "mcp__tierzero__update_jira_issue": "UpdateJiraIssue",
      "mcp__tierzero__add_jira_comment": "AddJiraComment",
      "mcp__tierzero__edit_jira_comment": "EditJiraComment",
      "mcp__tierzero__list_jira_projects": "ListJiraProjects",
      "mcp__tierzero__search_linear_issues": "SearchLinearIssues",
      "mcp__tierzero__get_linear_issue": "GetLinearIssue",
      "mcp__tierzero__create_linear_issue": "CreateLinearIssue",
      "mcp__tierzero__update_linear_issue": "UpdateLinearIssue",
      "mcp__tierzero__add_linear_comment": "AddLinearComment",
      "mcp__tierzero__edit_linear_comment": "EditLinearComment",
      "mcp__tierzero__list_linear_teams": "ListLinearTeams",
      "mcp__tierzero__list_linear_users": "ListLinearUsers",
      "mcp__tierzero__list_linear_projects": "ListLinearProjects",
      "mcp__tierzero__search_linear_projects": "SearchLinearProjects",
      "mcp__tierzero__get_linear_project": "GetLinearProject",
      "mcp__tierzero__create_linear_project": "CreateLinearProject",
      "mcp__tierzero__update_linear_project": "UpdateLinearProject",
      "mcp__tierzero__query_pganalyze_server_info": "QueryPGAnalyzeServerInfo",
      "mcp__tierzero__query_pganalyze_database_info": "QueryPGAnalyzeDatabaseInfo",
      "mcp__tierzero__query_pganalyze_schema": "QueryPGAnalyzeSchema",
      "mcp__tierzero__query_pganalyze_queries": "QueryPGAnalyzeQueries",
      "mcp__tierzero__query_pganalyze_issues": "QueryPGAnalyzeIssues",
      "mcp__tierzero__query_pganalyze_vacuums": "QueryPGAnalyzeVacuums",
      "mcp__tierzero__query_pganalyze_logs": "QueryPGAnalyzeLogs",
      "mcp__tierzero__query_pganalyze_connections": "QueryPGAnalyzeConnections",
      "mcp__tierzero__query_socket_alerts": "QuerySocketAlerts",
      "mcp__tierzero__list_socket_repos": "ListSocketRepos",
      "mcp__tierzero__query_socket_scan": "QuerySocketScan",
      "mcp__tierzero__search_web": "SearchWeb",
      "mcp__tierzero__fetch_web_content": "FetchWebContent",
      "mcp__tierzero__run_capability": "RunCapability",
      "mcp__tierzero__save_snippet": "SaveSnippet",
      "mcp__tierzero__RunCliCmd": "RunCliCmd",
      "mcp__tierzero__RunCustomCliCmd": "Run Custom Command",
      "mcp__tierzero__RunPostgresQuery": "RunPostgresQuery",
      "mcp__tierzero__list_custom_mcp_server_tools": "CustomMcpServer",
      "mcp__tierzero__call_custom_mcp_server_tool": "CustomMcpServer",
      "mcp__tierzero__propose_optimizer_config": "ProposeOptimizerConfig",
      "mcp__tierzero__save_optimizer_config": "SaveOptimizerConfig",
      "mcp__tierzero__save_optimizer_run_results": "SaveOptimizerRunResults",
      "mcp__tierzero__save_optimizer_cluster_result": "SaveOptimizerClusterResult",
      "mcp__tierzero__save_proposal_result": "SaveProposalResult",
      "mcp__tierzero__update_proposal_status": "UpdateProposalStatus",
      "mcp__tierzero__get_issue_data": "GetIssueData",
      "mcp__tierzero__deprecate_issue": "DeprecateIssue",
      "mcp__tierzero__update_issue_description": "UpdateIssueDescription",
    };

    // For custom MCP gateway tools, derive display name from server_slug
    if (toolName === "mcp__tierzero__list_custom_mcp_server_tools" || toolName === "mcp__tierzero__call_custom_mcp_server_tool") {
      try {
        const rawInput = toolCall?.toolUse?.input?.rawInput;
        if (rawInput) {
          const parsed = typeof rawInput === "string" ? JSON.parse(rawInput) : rawInput;
          return getCustomMcpServerDisplayName(parsed.server_slug) ?? "Custom MCP Server";
        }
      } catch {
        // fall through
      }
      return "Custom MCP Server";
    }

    // For RunCliCmd, derive display name from base_cmd
    if (toolName === "mcp__tierzero__RunCliCmd") {
      try {
        const rawInput = toolCall?.toolUse?.input?.rawInput;
        if (rawInput) {
          const parsed = typeof rawInput === "string" ? JSON.parse(rawInput) : rawInput;
          if (parsed.base_cmd === "gcloud") return "GCP";
          if (parsed.base_cmd === "kubectl") return "Kubernetes";
          if (parsed.base_cmd === "snow") return "Snowflake";
        }
      } catch {
        // fall through
      }
      return "AWS";
    }

    return mcpToolNameMap[toolName] || toolName;
  }

  return toolName;
}

/**
 * Extract question from a tool call
 */
export function getToolQuestion(toolCall?: ToolCall): string {
  const input = toolCall?.toolUse?.input;
  if (!input) return "";

  // Most tool types have a 'question' field
  if ("question" in input) {
    return input.question;
  }

  // Handle special cases without question field
  if ("usergroupHandle" in input) {
    return input.usergroupHandle;
  }

  return "";
}
